// A member's status from the office (BL-056): suspend (no renewal and no member prices, its visits
// still due) and resume; cancel today or from a later date, refunding what is unused by the plan's
// rule as a credit on their account, refunded from Money; and skip a due visit. Each is
// memberships.suspend, .resume, .cancel and .skipVisit; while the company mirrors ServiceTitan the
// server refuses them, in words. Drawn with ui-web's catalogued parts (BL-189): Button, Link, TextField
// and Notice; the forms' own lines are memberships.css's.

import { useState } from 'react';
import { Button, ConfirmDialog, Link, money, Notice, TextField } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { customerHref } from '../history/customer-link.ts';
import { FormActs } from './member-parts.tsx';
import {
  CANCEL_REFUND_WORDS,
  cancelDays,
  SALE_WORDS,
  IMPORTED_REFUND_NOTE,
  refundAsked,
  suspendNote,
  unusedCents,
  type CancelRefund,
  type MembershipDetail,
} from './model.ts';

interface StatusOut {
  status: string;
  creditCents: number;
  creditId: string | null;
  message: string;
}

/** Suspend… and Cancel… (or Resume), for a membership that is still current. */
export function MemberStatusActions({
  detail: d,
  name,
  today,
  onChanged,
}: {
  detail: MembershipDetail;
  /** "Ana Ruiz’s Comfort Club membership". */
  name: string;
  today: string;
  /** After a change: what was said, to read the membership again. */
  onChanged: (said: { message: string; customerId?: string; creditCents?: number }) => void;
}) {
  const [open, setOpen] = useState<'suspend' | 'cancel' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (d.status !== 'active' && d.status !== 'suspended') return null;
  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.resume', { membershipId: d.id });
      onChanged({ message: r.message });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (open === 'suspend')
    return <SuspendForm detail={d} name={name} onClose={() => setOpen(null)} onDone={onChanged} />;
  if (open === 'cancel')
    return (
      <CancelForm detail={d} name={name} today={today} onClose={() => setOpen(null)} onDone={onChanged} />
    );
  return (
    <div className="bp-member-acts">
      {d.status === 'active' && !d.cancelsOn && <Button onPress={() => setOpen('suspend')}>Suspend…</Button>}
      {d.status === 'suspended' && (
        <Button isBusy={busy} onPress={() => void resume()}>
          {busy ? 'Resuming…' : 'Resume'}
        </Button>
      )}
      {!d.cancelsOn && <Button onPress={() => setOpen('cancel')}>Cancel…</Button>}
      {error && <p className="bp-member-bad">{error}</p>}
    </div>
  );
}

function SuspendForm({
  detail: d,
  name,
  onClose,
  onDone,
}: {
  detail: MembershipDetail;
  name: string;
  onClose: () => void;
  onDone: (said: { message: string }) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.suspend', {
        membershipId: d.id,
        reason: reason.trim(),
      });
      onDone({ message: r.message });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="bp-member-form"
      aria-label={`Suspend ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="bp-member-line">
        While it is suspended, it doesn’t renew and member prices don’t apply. Its visits still come due and
        can be booked. {suspendNote(d.endsOn, dayText)}
      </p>
      <TextField
        label="Why"
        maxLength={500}
        value={reason}
        onChange={setReason}
        placeholder="Card declined, away for the winter…"
        error={tried && !reason.trim() ? 'Say why it is suspended.' : null}
      />
      <FormActs
        back={onClose}
        busy={busy}
        error={error}
        label={busy ? 'Suspending…' : 'Suspend it'}
        onSave={() => void save()}
      />
    </form>
  );
}

function CancelForm({
  detail: d,
  name,
  today,
  onClose,
  onDone,
}: {
  detail: MembershipDetail;
  name: string;
  today: string;
  onClose: () => void;
  onDone: (said: { message: string; customerId?: string; creditCents?: number }) => void;
}) {
  // the plan's rule at a cancel, read now: it is the company's policy, not the sale's
  const plans = useQuery<{ plans: { id: string; cancelRefund?: CancelRefund }[] }>(
    'membershipTypes.list',
    {},
  );
  const planRule: CancelRefund = plans.data?.plans.find((p) => p.id === d.typeId)?.cancelRefund ?? 'optional';
  const unused = unusedCents(d);
  // a sale voided (or still a draft) defers nothing: there is nothing to refund, whatever the rule
  const saleSays = d.sale ? SALE_WORDS[d.sale] : undefined;
  const rule: CancelRefund = saleSays ? 'never' : planRule;
  const days = cancelDays(today, d.endsOn);
  const [on, setOn] = useState(today);
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState((unused / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const asked = refundAsked(rule, refund, unused);
  const later = on > today;
  const problems = [
    ...(!reason.trim() ? ['Say why it is cancelled.'] : []),
    ...(on < days.min || on > days.max ? [`A day from ${dayText(days.min)} to ${dayText(days.max)}.`] : []),
    ...(asked.problem ? [asked.problem] : []),
  ];
  const save = async () => {
    setTried(true);
    if (problems.length || !plans.data) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.cancel', {
        membershipId: d.id,
        reason: reason.trim(),
        ...(later ? { effectiveOn: on } : {}),
        ...(asked.cents !== undefined ? { refundCents: asked.cents } : {}),
      });
      onDone({ message: r.message, customerId: d.customerId, creditCents: r.creditCents });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="bp-member-form"
      aria-label={`Cancel ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <TextField
        type="date"
        width="date"
        label="From"
        min={days.min}
        max={days.max}
        value={on}
        onChange={setOn}
      />
      <TextField
        label="Why"
        maxLength={500}
        value={reason}
        onChange={setReason}
        placeholder="Moving away, not using it…"
      />
      <p className="bp-member-line">
        It stops renewing now.
        {later ? ` On ${dayText(on)}` : ' Today'} its visits stop, and a visit job booked from that day on
        that hasn’t started is cancelled.
      </p>
      <p className="bp-member-line">
        {money(unused)} of {money(d.deferredCents)} paid for the term isn’t used yet (before tax).
        <span className="bp-member-quiet bp-member-block">Plan rule: {CANCEL_REFUND_WORDS[planRule]}.</span>
      </p>
      {saleSays ? (
        <Notice title={saleSays} />
      ) : rule === 'optional' ? (
        <TextField
          label="Refund as a credit on their account"
          inputMode="decimal"
          width="number"
          value={refund}
          onChange={setRefund}
          description={`At most ${money(unused)}${later ? ', or what is unused by then' : ''}. The rest is kept as income.`}
        />
      ) : rule === 'always' ? (
        <p className="bp-member-line">
          {money(unused)} {later ? '(or what is unused by then) ' : ''}is refunded as a credit on their
          account.
        </p>
      ) : (
        <p className="bp-member-line">Nothing is refunded: what is unused is kept as income.</p>
      )}
      {!d.invoiceId && rule !== 'never' && <p className="bp-member-quiet">{IMPORTED_REFUND_NOTE}</p>}
      {rule !== 'never' && (
        <p className="bp-member-quiet">
          The credit is refunded from their Money tab. A sale still owing takes it off what is owed first.
        </p>
      )}
      {tried && problems.length > 0 && (
        <ul className="bp-member-bad" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <FormActs
        back={onClose}
        busy={busy}
        error={error}
        label={
          d.sale === 'draft'
            ? null
            : busy
              ? 'Cancelling…'
              : later
                ? `Cancel it from ${dayText(on)}`
                : 'Cancel it today'
        }
        onSave={() => void save()}
      />
    </form>
  );
}

/** What a change said, with the way to the credit when one was left on their account. */
export function StatusSaid({
  said,
}: {
  said: { message: string; customerId?: string; creditCents?: number } | null;
}) {
  if (!said) return null;
  return (
    <Notice
      tone="done"
      title={said.message}
      {...(said.creditCents && said.customerId
        ? { actions: <Link href={customerHref(said.customerId, 'money')}>Open their Money tab</Link> }
        : {})}
    />
  );
}

/** Skip… on a due visit: counted as used and its share recognised, the next one comes due. It asks first,
 * in a ConfirmDialog. */
export function SkipVisit({
  recurringServiceId,
  what,
  onDone,
}: {
  recurringServiceId: string;
  /** "Ana Ruiz’s tune-up due Nov 20". */
  what: string;
  onDone: (said: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const skip = async () => {
    try {
      const r = await client.call<{ nextDueOn: string }>('memberships.skipVisit', { recurringServiceId });
      onDone(`Skipped ${what}: it counts as used. The next one is due ${dayText(r.nextDueOn)}.`);
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
  };
  return (
    <>
      <Link label={`Skip ${what}`} onPress={() => setAsking(true)}>
        Skip…
      </Link>
      <ConfirmDialog
        isOpen={asking}
        onClose={() => setAsking(false)}
        label={`Skip ${what}?`}
        title={`Skip ${what}?`}
        confirmLabel="Skip it"
        cancelLabel="Back"
        onConfirm={skip}
      >
        It counts as used, as if done.
      </ConfirmDialog>
    </>
  );
}
